// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/** Turn user-typed text into bytes under a chosen interpretation. Pure + tested. */
import type { TextInputMode } from './types';

const utf8 = new TextEncoder();

/** Decode `text` into bytes as UTF-8, hex, or base64. Throws on malformed hex/base64. */
export function decodeInput(text: string, mode: TextInputMode): Uint8Array {
  switch (mode) {
    case 'utf8':
      return utf8.encode(text);
    case 'hex':
      return hexToBytes(text);
    case 'base64':
      return base64ToBytes(text);
  }
}

export function hexToBytes(input: string): Uint8Array {
  // Strip whitespace, colons and an optional 0x prefix, keep hex digits.
  const hex = input.replace(/0x/gi, '').replace(/[\s:]/g, '');
  if (hex.length === 0) return new Uint8Array(0);
  if (hex.length % 2 !== 0) throw new Error('Hex input must have an even number of digits');
  if (!/^[0-9a-fA-F]+$/.test(hex)) throw new Error('Hex input contains non-hex characters');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function base64ToBytes(input: string): Uint8Array {
  const clean = input.trim().replace(/\s/g, '');
  if (clean.length === 0) return new Uint8Array(0);
  // Accept both standard and URL-safe base64.
  const normalized = clean.replace(/-/g, '+').replace(/_/g, '/');
  let binary: string;
  try {
    binary = atob(normalized);
  } catch {
    throw new Error('Base64 input is not valid');
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** Lowercase hex encoding of raw bytes. */
export function bytesToHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}
